import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { FC, useEffect, useRef, useState, type JSX } from 'react';
import { useAppSelector } from '@/store/hooks/redux';
import EmptyImageCard from '../EmptyImageCard';
import { IExtendedPreviewPropsModel } from '@/editor/shared/components/forms/creatives/models/basic-preview-props.model';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CreativeTypesEnum from '@/editor/shared/constants/creatives-types.enum';

const WebmegleBidtheatrePreview: FC<IExtendedPreviewPropsModel> = ({
  caption,
  description,
  propertyPriceQuote,
  propertyType,
  areaInUsage,
  propertyDebt,
  ownershipType,
  viewDate,
  selectedType
}): JSX.Element => {

  const singleMedia = useAppSelector(
    (state) => state.creativeStepSliceReducer?.creativeFormsState?.bidtheatre?.single?.media
  );

  const dynamicMedia = useAppSelector(
    (state) => state.creativeStepSliceReducer?.creativeFormsState?.bidtheatre?.dynamic?.creativeMedias
  );

  const bidtheatreShareLink = useAppSelector((state) => state.propertyInfoSliceReducer.bidtheatreShareLink);

  const [index, setIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const totalSlides = dynamicMedia.length;

  useEffect(() => {
    if (selectedType === CreativeTypesEnum.DYNAMIC) {
      startAutoSlide();
      return stopAutoSlide;
    }
  }, [selectedType]);

  const startAutoSlide = () => {
    intervalRef.current = setInterval(() => {
      handleNext();
    }, 4000);
  };

  const stopAutoSlide = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
  };

  const handleNext = () => {
    if (index === totalSlides) return;
    setIsTransitioning(true);
    setIndex((prev) => prev + 1);
  };

  useEffect(() => {
    if (index === totalSlides) {
      const timeout = setTimeout(() => {
        setIsTransitioning(false);
        setIndex(0);
      }, 600);
      return () => clearTimeout(timeout);
    }
  }, [index, totalSlides]);

  return (
    <div>
      <Grid container sx={{ overflow: 'hidden', position: 'relative' }}>
        {selectedType === CreativeTypesEnum.SINGLE || selectedType === CreativeTypesEnum.CAROUSEL || selectedType == CreativeTypesEnum.VIDEO ? (
          singleMedia?.preSignedUrl ? (
            <Grid
              item
              component="a"
              target="_blank"
              href={bidtheatreShareLink}
              xs={12}
              className="eie-preview-box"
              sx={{
                backgroundImage: `url(${singleMedia.preSignedUrl})`,
                textDecoration: 'none',
              }}>
              <Box className="eie-preview-box__button-container">
                <Button className="eie-preview-box__button-container__button">
                  <Typography variant="h4">Se bolig</Typography>
                </Button>
              </Box>
            </Grid>
          ) : (
            <EmptyImageCard />
          )
        ) : dynamicMedia?.[0]?.preSignedUrl ? (
          <Grid
            item
            component="a"
            target="_blank"
            href={bidtheatreShareLink}
            xs={12}
            className="eie-preview-box">
            <Box
              sx={{
                width: '750px',
                height: '416px',
                overflow: 'hidden',
                margin: 'auto',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  width: `${750 * (totalSlides + 1)}px`,
                  transform: `translateX(-${index * 750}px)`,
                  transition: isTransitioning ? 'transform 0.6s ease-in-out' : 'none',
                }}
                onTransitionEnd={() => setIsTransitioning(true)}
              >
                {dynamicMedia.map((src, i) => (
                  <img
                    key={i}
                    src={src.preSignedUrl}
                    alt={`slide-${i}`}
                    style={{ width: '750px', height: '384px', objectFit: 'cover' }}
                  />
                ))}
                <img
                  src={dynamicMedia[0].preSignedUrl}
                  alt="cloned-slide"
                  style={{ width: '750px', height: '384px', objectFit: 'cover' }}
                />
              </Box>
              <Box
                sx={{
                  position: 'absolute',
                  top: '40%',
                  left: 10,
                  backgroundColor: 'rgba(0, 0, 0, 0.4)',
                  color: '#fff',
                }}
              >
                <ChevronLeftIcon sx={{ fontSize: '35px' }} />
              </Box>
              <Box
                sx={{
                  position: 'absolute',
                  top: '40%',
                  right: 10,
                  backgroundColor: 'rgba(0, 0, 0, 0.4)',
                  color: '#fff',
                }}
              >
                <ChevronRightIcon sx={{ fontSize: '35px' }} />
              </Box>
            </Box>
          </Grid>
        ) : (
          <EmptyImageCard />
        )}
        <Grid item xs={12}>
          <Grid container spacing={2} sx={{ backgroundColor: '#0a1119' }}>
            <Grid item xs={3}>
              <img style={{ width: '100%' }} src="/images/eie/logo-with-text.png" alt="logo" className="logo" />
            </Grid>
            <Grid item xs={9}>
              <Grid container flexDirection="column">
                <Grid item xs={12}>
                  <Typography
                    className={`${viewDate && 'animated-words'}`}
                    variant="h3"
                    fontFamily="saol"
                    fontWeight="bold"
                    color="#fff">
                    <span>{caption && <div>{caption}</div>}</span>
                    <span>{viewDate && <div>{viewDate}</div>}</span>
                  </Typography>
                  <Typography
                    className={`${ownershipType && 'animated-words'}`}
                    variant="h3"
                    fontFamily="saol"
                    fontWeight="bold"
                    color="#fff">
                    <span>
                      {propertyType || areaInUsage ? (
                        <div>
                          {propertyType?.toUpperCase()} {areaInUsage}
                        </div>
                      ) : null}
                    </span>
                    <span>{ownershipType && <div>{ownershipType}</div>}</span>
                  </Typography>
                  <Typography variant="h6" fontFamily="Helvetica" color="#fff">
                    {description && description.length > 50 ? description.slice(0, 50) + '...' : description}
                  </Typography>
                  <Typography
                    className={`${propertyDebt && 'animated-words'}`}
                    variant="h4"
                    fontFamily="saol"
                    fontWeight="bold"
                    color="#fff">
                    <span>{propertyPriceQuote && <div> {propertyPriceQuote.toLocaleString()}</div>}</span>
                    <span>{propertyDebt && <div> {propertyDebt.toLocaleString()}</div>}</span>
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

export default WebmegleBidtheatrePreview;
